interface GeneratePasswordOptions {
    length?: number;
    ambiguous?: boolean;
    number?: boolean;
    minNumber?: number;
    uppercase?: boolean;
    minUppercase?: number;
    lowercase?: boolean;
    minLowercase?: number;
    special?: boolean;
    minSpecial?: number;
    minZoomSpecial?: number;
}

// Based on https://github.com/bitwarden/jslib/blob/master/common/src/services/passwordGeneration.service.ts
export class PasswordGenerator {
    readonly defaultOptions = {
        length: 14,
        ambiguous: false,
        number: true,
        minNumber: 1,
        uppercase: true,
        minUppercase: 1,
        lowercase: true,
        minLowercase: 1,
        special: true,
        minSpecial: 1
    };

    generatePassword(options?: GeneratePasswordOptions): string {
        const opts = { ...this.defaultOptions, ...options };

        const minLength: number = opts.minUppercase + opts.minLowercase + opts.minNumber + opts.minSpecial;
        if (opts.length < minLength) {
            opts.length = minLength;
        }

        const positions: string[] = [];
        if (opts.lowercase && opts.minLowercase > 0) {
            for (let i = 0; i < opts.minLowercase; i++) {
                positions.push('l');
            }
        }
        if (opts.uppercase && opts.minUppercase > 0) {
            for (let i = 0; i < opts.minUppercase; i++) {
                positions.push('u');
            }
        }
        if (opts.number && opts.minNumber > 0) {
            for (let i = 0; i < opts.minNumber; i++) {
                positions.push('n');
            }
        }
        if (opts.special && opts.minSpecial > 0) {
            for (let i = 0; i < opts.minSpecial; i++) {
                positions.push('s');
            }
        }
        while (positions.length < opts.length) {
            positions.push('a');
        }

        // shuffle
        PasswordGenerator.shuffleArray(positions);

        // build out the char sets
        let allCharSet = '';

        let lowercaseCharSet = 'abcdefghijkmnopqrstuvwxyz';
        if (opts.ambiguous) {
            lowercaseCharSet += 'l';
        }
        if (opts.lowercase) {
            allCharSet += lowercaseCharSet;
        }

        let uppercaseCharSet = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
        if (opts.ambiguous) {
            uppercaseCharSet += 'IO';
        }
        if (opts.uppercase) {
            allCharSet += uppercaseCharSet;
        }

        let numberCharSet = '23456789';
        if (opts.ambiguous) {
            numberCharSet += '01';
        }
        if (opts.number) {
            allCharSet += numberCharSet;
        }

        const specialCharSet = '!@#$%^&*';
        if (opts.special) {
            allCharSet += specialCharSet;
        }

        let password = '';
        for (let i = 0; i < opts.length; i++) {
            let positionChars = allCharSet;

            switch (positions[i]) {
                case 'l': positionChars = lowercaseCharSet; break;
                case 'u': positionChars = uppercaseCharSet; break;
                case 'n': positionChars = numberCharSet; break;
                case 's': positionChars = specialCharSet; break;
                case 'a': positionChars = allCharSet; break;
                default: break;
            }

            password += positionChars.charAt(Math.floor(Math.random() * positionChars.length));
        }

        return password;
    }

    // ref: https://stackoverflow.com/a/12646864/1090359
    private static shuffleArray(array: string[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));

            // eslint-disable-next-line no-param-reassign
            [array[i], array[j]] = [array[j], array[i]];
        }
    }
}
